<script setup lang="ts">
import { ref } from 'vue'

const features = [
  {
    id: 'pos',
    name: 'Point of Sale',
    icon: '🏪',
    screenshot: '/screenshots/pos.jpg',
    description: 'Modern and intuitive POS interface with powerful features'
  },
  {
    id: 'inventory',
    name: 'Inventory Management',
    icon: '📦',
    screenshot: '/screenshots/inventory.jpg',
    description: 'Real-time inventory tracking and management'
  },
  {
    id: 'accounting',
    name: 'Financial Management',
    icon: '📊',
    screenshot: '/screenshots/accounting.jpg',
    description: 'Comprehensive accounting and reporting tools'
  },
  {
    id: 'crm',
    name: 'Customer Management',
    icon: '👥',
    screenshot: '/screenshots/crm.jpg',
    description: 'Advanced customer relationship management'
  },
  {
    id: 'analytics',
    name: 'Business Analytics',
    icon: '📈',
    screenshot: '/screenshots/analytics.jpg',
    description: 'Powerful insights and reporting dashboard'
  }
]

const selectedFeature = ref(features[0])

const selectFeature = (feature: typeof features[0]) => {
  selectedFeature.value = feature
}
</script>

<template>
  <section class="py-16 bg-white dark:bg-gray-900">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="text-center mb-12">
        <h2 class="text-3xl font-bold text-gray-900 dark:text-white mb-4">
          Explore More
        </h2>
        <p class="text-lg text-gray-600 dark:text-gray-400">
          Take a closer look at our powerful features
        </p>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Feature List -->
        <div class="space-y-2">
          <button
            v-for="feature in features"
            :key="feature.id"
            @click="selectFeature(feature)"
            class="w-full p-4 rounded-lg text-left transition-colors flex items-center gap-4"
            :class="[
              selectedFeature.id === feature.id
                ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400'
                : 'hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400'
            ]"
          >
            <span class="text-2xl">{{ feature.icon }}</span>
            <div>
              <h3 class="font-medium" :class="[
                selectedFeature.id === feature.id
                  ? 'text-blue-600 dark:text-blue-400'
                  : 'text-gray-900 dark:text-white'
              ]">
                {{ feature.name }}
              </h3>
              <p class="text-sm text-gray-500 dark:text-gray-400">
                {{ feature.description }}
              </p>
            </div>
          </button>
        </div>

        <!-- Screenshot Display -->
        <div class="lg:col-span-2 relative">
          <div class="aspect-video bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden">
            <!-- Placeholder for actual screenshots -->
            <div class="absolute inset-0 flex items-center justify-center text-gray-400 dark:text-gray-600 text-9xl">
              {{ selectedFeature.icon }}
            </div>
            <img
              :src="selectedFeature.screenshot"
              :alt="selectedFeature.name"
              class="absolute inset-0 w-full h-full object-cover opacity-80"
            />
          </div>
          
          <!-- Feature Info Overlay -->
          <div class="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/70 to-transparent text-white">
            <h3 class="text-2xl font-semibold mb-2">{{ selectedFeature.name }}</h3>
            <p>{{ selectedFeature.description }}</p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>